import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { API, apiError } from "@/lib/api";
import { money, dateFr } from "@/lib/format";
import LineItemsEditor from "@/components/LineItemsEditor";
import StatusBadge from "@/components/StatusBadge";
import SendDialog from "@/components/SendDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, DotsThreeVertical, FilePdf, PaperPlaneTilt, CheckCircle, PencilSimple, Trash, ArrowCounterClockwise, UploadSimple, Sparkle } from "@phosphor-icons/react";

const emptyForm = { client_id: "", issue_date: "", due_date: "", notes: "", status: "draft" };
const newItems = () => [{ description: "", quantity: 1, unit_price: 0, taxable: true }];
const FILTERS = [
  { v: "all", l: "Toutes" }, { v: "draft", l: "Brouillons" }, { v: "sent", l: "Envoyées" },
  { v: "paid", l: "Payées" },
];

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [items, setItems] = useState(newItems());
  const [editId, setEditId] = useState(null);
  const [delId, setDelId] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [impClient, setImpClient] = useState({ name: "", email: "", address: "", phone: "" });
  const [impMatchId, setImpMatchId] = useState(null);
  const [impForm, setImpForm] = useState({ issue_date: "", notes: "", status: "paid" });
  const [impItems, setImpItems] = useState(newItems());
  const importFileRef = useRef();

  const load = () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/invoices${q}`).then((r) => setInvoices(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filter]);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);

  const openNew = () => { setForm(emptyForm); setItems(newItems()); setEditId(null); setOpen(true); };
  const openEdit = (inv) => {
    setForm({ client_id: inv.client_id, issue_date: inv.issue_date, due_date: inv.due_date, notes: inv.notes || "", status: inv.status });
    setItems(inv.line_items.map((i) => ({ ...i })));
    setEditId(inv.id); setOpen(true);
  };

  const save = async () => {
    if (!form.client_id) { toast.error("Sélectionnez un client."); return; }
    if (!items.length || !items.some((i) => i.description)) { toast.error("Ajoutez au moins une ligne."); return; }
    setSaving(true);
    const payload = {
      client_id: form.client_id,
      issue_date: form.issue_date || null,
      due_date: form.due_date || null,
      notes: form.notes,
      status: form.status,
      line_items: items.map((i) => ({ description: i.description, quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0, taxable: i.taxable !== false })),
    };
    try {
      if (editId) await api.put(`/invoices/${editId}`, payload);
      else await api.post("/invoices", payload);
      toast.success(editId ? "Facture mise à jour" : "Facture créée");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const viewPdf = (inv) => {
    const token = localStorage.getItem("token");
    window.open(`${API}/invoices/${inv.id}/pdf?auth=${token}`, "_blank");
  };
  const send = (inv) => setSendTarget({ kind: "invoice", doc: inv });
  const markPaid = async (inv) => { try { await api.post(`/invoices/${inv.id}/mark-paid`); toast.success("Marquée payée"); load(); } catch (e) { toast.error(apiError(e)); } };
  const markUnpaid = async (inv) => { try { await api.post(`/invoices/${inv.id}/mark-unpaid`); toast.success("Marquée impayée"); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async () => { try { await api.delete(`/invoices/${delId}`); toast.success("Facture supprimée"); setDelId(null); load(); } catch (e) { toast.error(apiError(e)); } };

  const onImportFile = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    setImporting(true);
    toast.loading("Lecture de la facture par l'IA…", { id: "imp" });
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/invoices/import-extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImpClient(data.client || { name: "", email: "", address: "", phone: "" });
      setImpMatchId(data.matched_client_id || null);
      setImpForm({ issue_date: data.issue_date || "", notes: data.notes || "", status: "paid" });
      setImpItems((data.line_items && data.line_items.length ? data.line_items : newItems()).map((i) => ({ ...i, taxable: i.taxable !== false })));
      setImportOpen(true);
      toast.success("Facture lue — vérifiez les informations", { id: "imp" });
    } catch (e) { toast.error(apiError(e), { id: "imp" }); }
    finally { setImporting(false); if (importFileRef.current) importFileRef.current.value = ""; }
  };

  const saveImport = async () => {
    if (!impClient.name) { toast.error("Le nom du client est requis."); return; }
    if (!impItems.some((i) => i.description)) { toast.error("Ajoutez au moins une ligne."); return; }
    const payload = {
      client_id: impMatchId || null,
      client: impClient,
      issue_date: impForm.issue_date || null,
      notes: impForm.notes,
      status: impForm.status,
      line_items: impItems.map((i) => ({ description: i.description, quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0, taxable: i.taxable !== false })),
    };
    try {
      const { data } = await api.post("/invoices/import-save", payload);
      toast.success(`Facture ${data.invoice_number} importée`);
      setImportOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Factures</h1>
          <p className="text-muted-foreground mt-1">Créez, importez, envoyez et suivez vos factures.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={importFileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onImportFile} data-testid="import-file-input" />
          <Button data-testid="import-invoice-btn" variant="outline" className="rounded-sm" onClick={() => importFileRef.current?.click()} disabled={importing}>
            <UploadSimple size={18} className="mr-1" /> {importing ? "Lecture…" : "Importer (IA)"}
          </Button>
          <Button data-testid="new-invoice-btn" className="rounded-sm" onClick={openNew}>
            <Plus size={18} className="mr-1" /> Nouvelle facture
          </Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.v}
            data-testid={`filter-${f.v}`}
            onClick={() => setFilter(f.v)}
            className={`px-3 py-1.5 rounded-sm text-sm border transition-colors ${filter === f.v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Numéro</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Émission</TableHead>
              <TableHead>Échéance</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Aucune facture.</TableCell></TableRow>
            )}
            {invoices.map((inv) => (
              <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                <TableCell className="font-mono font-medium">{inv.invoice_number}</TableCell>
                <TableCell>{inv.client?.name}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{dateFr(inv.issue_date)}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{dateFr(inv.due_date)}</TableCell>
                <TableCell className="text-right tabular-nums font-medium">{money(inv.total)}</TableCell>
                <TableCell><StatusBadge status={inv.status} /></TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button data-testid={`actions-${inv.id}`} variant="ghost" size="icon" className="h-8 w-8"><DotsThreeVertical size={18} weight="bold" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-sm">
                      <DropdownMenuItem data-testid={`view-pdf-${inv.id}`} onClick={() => viewPdf(inv)}><FilePdf size={16} className="mr-2" /> Voir le PDF</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`send-${inv.id}`} onClick={() => send(inv)}><PaperPlaneTilt size={16} className="mr-2" /> Envoyer par courriel</DropdownMenuItem>
                      {inv.status !== "paid"
                        ? <DropdownMenuItem data-testid={`paid-${inv.id}`} onClick={() => markPaid(inv)}><CheckCircle size={16} className="mr-2" /> Marquer payée</DropdownMenuItem>
                        : <DropdownMenuItem onClick={() => markUnpaid(inv)}><ArrowCounterClockwise size={16} className="mr-2" /> Marquer impayée</DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => openEdit(inv)}><PencilSimple size={16} className="mr-2" /> Modifier</DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={() => setDelId(inv.id)}><Trash size={16} className="mr-2" /> Supprimer</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Modifier la facture" : "Nouvelle facture"}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-1.5 md:col-span-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Client *</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="invoice-client-select" className="rounded-sm"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Date d'émission</Label>
                <Input data-testid="invoice-issue-date" type="date" className="rounded-sm" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Échéance</Label>
                <Input data-testid="invoice-due-date" type="date" className="rounded-sm" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2 block">Lignes</Label>
              <LineItemsEditor items={items} setItems={setItems} />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Notes</Label>
                <Textarea data-testid="invoice-notes" className="rounded-sm" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="invoice-status-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Brouillon</SelectItem>
                    <SelectItem value="sent">Envoyée</SelectItem>
                    <SelectItem value="paid">Payée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-invoice-btn" className="rounded-sm" onClick={save} disabled={saving}>{saving ? "Enregistrement…" : "Enregistrer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Sparkle size={20} /> Importer une facture — vérification</DialogTitle></DialogHeader>
          <div className="space-y-5">
            {impMatchId
              ? <div className="text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm px-3 py-2">Client existant reconnu — la facture y sera rattachée (aucun doublon créé).</div>
              : <div className="text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded-sm px-3 py-2">Aucun client existant reconnu automatiquement. Choisissez-en un ci-dessous, sinon un nouveau client sera créé.</div>}
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2 block">Client</Label>
              <Select value={impMatchId || "new"} onValueChange={(v) => {
                if (v === "new") { setImpMatchId(null); return; }
                const c = clients.find((x) => x.id === v);
                setImpMatchId(v);
                if (c) setImpClient({ name: c.name, email: c.email || "", address: c.address || "", phone: c.phone || "" });
              }}>
                <SelectTrigger data-testid="import-client-select" className="rounded-sm mb-3"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">Nouveau client (depuis la facture)</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="grid md:grid-cols-2 gap-3">
                <Input data-testid="import-client-name" className="rounded-sm" placeholder="Nom du client *" value={impClient.name} onChange={(e) => { setImpClient({ ...impClient, name: e.target.value }); setImpMatchId(null); }} />
                <Input data-testid="import-client-email" className="rounded-sm" placeholder="Courriel" value={impClient.email} onChange={(e) => setImpClient({ ...impClient, email: e.target.value })} />
                <Input className="rounded-sm" placeholder="Adresse" value={impClient.address} onChange={(e) => setImpClient({ ...impClient, address: e.target.value })} />
                <Input className="rounded-sm" placeholder="Téléphone" value={impClient.phone} onChange={(e) => setImpClient({ ...impClient, phone: e.target.value })} />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Date d'émission</Label>
                <Input data-testid="import-issue-date" type="date" className="rounded-sm" value={impForm.issue_date} onChange={(e) => setImpForm({ ...impForm, issue_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Statut</Label>
                <Select value={impForm.status} onValueChange={(v) => setImpForm({ ...impForm, status: v })}>
                  <SelectTrigger data-testid="import-status" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">Payée</SelectItem>
                    <SelectItem value="sent">Envoyée</SelectItem>
                    <SelectItem value="draft">Brouillon</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2 block">Lignes (prix avant taxes)</Label>
              <LineItemsEditor items={impItems} setItems={setImpItems} />
            </div>
            <Textarea data-testid="import-notes" className="rounded-sm" placeholder="Notes" value={impForm.notes} onChange={(e) => setImpForm({ ...impForm, notes: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setImportOpen(false)}>Annuler</Button>
            <Button data-testid="save-import-btn" className="rounded-sm" onClick={saveImport}>Importer la facture</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <SendDialog target={sendTarget} onOpenChange={(v) => !v && setSendTarget(null)} onSent={load} />
      <AlertDialog open={!!delId} onOpenChange={(v) => !v && setDelId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette facture ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-invoice" className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={del}>Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
